import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Linking,
  SafeAreaView,
  Share,
  Dimensions,
  Platform,
  Alert,
} from "react-native";
import { useAuth } from "../contexts/AuthContext";
import { useFavorites } from "../contexts/FavoritesContext";
import { Ionicons } from "@expo/vector-icons";
import { Opportunity } from "../types";
import { format, parseISO, differenceInDays } from "date-fns";
import FeedbackModal from "./FeedbackModal";
import AuthModal from "./auth/AuthModal";
import AuthRequiredModal from "./auth/AuthRequiredModal";
import {
  calculateOpportunityStatus,
  getDaysUntilDate,
} from "../utils/filterUtils";
import {
  trackApplyNowClick,
  trackShare,
  trackPageView,
} from "../utils/analyticsUtils";
import * as Animatable from "react-native-animatable";
import { useTheme } from "../contexts/ThemeContext";
import { updateOpportunitySEO } from "../utils/seoUtils";

const { width: screenWidth } = Dimensions.get("window");
const isLargeScreen = screenWidth > 900;
const isMediumScreen = screenWidth > 600 && screenWidth <= 900;
const isSmallScreen = screenWidth <= 600;
const isVerySmallScreen = screenWidth < 375;

// Color palette for light/dark mode with optimized dark theme
const colors = {
  light: {
    background: "#f8fafc",
    card: "#FFF",
    header: "#FFFFFF",
    border: "#E4E4E7",
    text: "#1F2937",
    textSecondary: "#4B5563",
    badge: "#F0F0F0",
    badgeText: "#4B5563",
    orgText: "#4B5563",
    infoLabel: "#64748b",
    infoValue: "#334155",
    sectionTitle: "#334155",
    desc: "#475569",
    addCard: "#f1f5f9",
    tag: "#f1f5f9",
    tagText: "#64748b",
    claimBox: "#eff6ff",
    claimBorder: "#bfdbfe",
    claimTitle: "#1e40af",
    claimDesc: "#3b82f6",
    claimBtn: "#3498db",
    claimBtnText: "#3498db",
    footerText: "#94a3b8",
    reportBtn: "#F1F5F9",
    reportBtnText: "#64748b",
    stickyBg: "rgba(255,255,255,0.95)",
    stickyBorder: "#E2E8F0",
    closeBtn: "#F4F4F5",
    actionBtn: "#F4F4F5",
    urgentBg: "#ef4444",
    urgentBorder: "#b91c1c",
    urgentLabel: "#fff",
    urgentDate: "#fff",
    deadlineBg: "#fff8e1",
    deadlineBorder: "#d97706",
    deadlineLabel: "#d97706",
    deadlineDate: "#92400e",
    shadow: "#000",
  },
  dark: {
    background: "#18181B",
    card: "#27272A",
    header: "#1F1F23",
    border: "#3A3A3D",
    text: "#FAFAFA",
    textSecondary: "#D4D4D8",
    badge: "#2A2A2E",
    badgeText: "#D4D4D8",
    orgText: "#D4D4D8",
    infoLabel: "#A1A1AA",
    infoValue: "#FAFAFA",
    sectionTitle: "#FAFAFA",
    desc: "#D4D4D8",
    addCard: "#232327",
    tag: "#2A2A2E",
    tagText: "#D4D4D8",
    claimBox: "#1E293B",
    claimBorder: "#334155",
    claimTitle: "#93C5FD",
    claimDesc: "#93C5FD",
    claimBtn: "#60A5FA",
    claimBtnText: "#60A5FA",
    footerText: "#A1A1AA",
    reportBtn: "#2A2A2E",
    reportBtnText: "#D4D4D8",
    stickyBg: "rgba(24,24,27,0.95)",
    stickyBorder: "#3A3A3D",
    closeBtn: "#2A2A2E",
    actionBtn: "#2A2A2E",
    urgentBg: "#EF4444",
    urgentBorder: "#DC2626",
    urgentLabel: "#FAFAFA",
    urgentDate: "#FAFAFA",
    deadlineBg: "#1E40AF",
    deadlineBorder: "#1D4ED8",
    deadlineLabel: "#FAFAFA",
    deadlineDate: "#FAFAFA",
    shadow: "rgba(0,0,0,0.3)",
  },
};

interface OpportunityDetailProps {
  opportunity: Opportunity;
  onClose: () => void;
  isModal?: boolean;
}

const OpportunityDetail: React.FC<OpportunityDetailProps> = ({
  opportunity,
  onClose,
  isModal = true,
}) => {
  const { theme } = useTheme();
  const c = colors[theme];
  // Get authentication status
  const { isAuthenticated } = useAuth();
  const { saveOpportunity, unsaveOpportunity, isSaved } = useFavorites();
  const [isFavorite, setIsFavorite] = useState(isSaved(opportunity.id));

  // State for modals and tabs
  const [isReportFormVisible, setIsReportFormVisible] = useState(false);
  const [isAuthModalVisible, setIsAuthModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState("details"); // 'details' or 'eligibility'
  const [authModalFeature, setAuthModalFeature] = useState(""); // Track which feature triggered auth modal

  // Track page view when opportunity detail is opened
  useEffect(() => {
    trackPageView("OpportunityDetail", `/opportunity/${opportunity.id}`);
  }, [opportunity.id]);

  // Add smooth transitions for web theme switching
  useEffect(() => {
    if (Platform.OS === "web" && typeof document !== "undefined") {
      // Add transition styles for smooth theme switching in detail view
      if (!document.getElementById("detail-theme-transitions")) {
        const transitionStyles = document.createElement("style");
        transitionStyles.id = "detail-theme-transitions";
        transitionStyles.innerHTML = `
          .opportunity-detail * {
            transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease !important;
          }
        `;
        document.head.appendChild(transitionStyles);
      }

      // Add class to container for targeting
      const container = document.querySelector(".opportunity-detail-container");
      if (container) {
        container.classList.add("opportunity-detail");
      }
    }
  }, [theme]);

  // Update SEO when opportunity changes
  useEffect(() => {
    if (opportunity && Platform.OS === "web") {
      // Update SEO for this opportunity
      updateOpportunitySEO(opportunity);
    }

    // Cleanup function to restore original SEO when modal closes
    return () => {
      if (Platform.OS === "web" && isModal) {
        // Reset to default page title and meta tags when closing modal
        document.title =
          "BC STEM Opportunities | Discover Your Next Opportunity";

        // Reset meta description
        const metaDescription = document.querySelector(
          'meta[name="description"]',
        ) as HTMLMetaElement;
        if (metaDescription) {
          metaDescription.setAttribute(
            "content",
            "Discover STEM opportunities across British Columbia. Find competitions, grants, scholarships, hackathons, internships, and workshops in one centralized platform.",
          );
        }

        // Reset canonical URL
        const canonicalLink = document.querySelector(
          'link[rel="canonical"]',
        ) as HTMLLinkElement;
        if (canonicalLink) {
          canonicalLink.setAttribute("href", "https://bcstem.ca/");
        }

        // Remove opportunity-specific structured data
        const structuredDataScript = document.querySelector(
          'script[data-id="opportunity-structured-data"]',
        );
        if (structuredDataScript) {
          structuredDataScript.remove();
        }

        const breadcrumbScript = document.querySelector(
          'script[data-id="breadcrumb-data"]',
        );
        if (breadcrumbScript) {
          breadcrumbScript.remove();
        }
      }
    };
  }, [opportunity, isModal]);

  // Helper to get primary type from possibly multiple types
  const getPrimaryType = (): string => {
    if (Array.isArray(opportunity.type)) {
      return opportunity.type.length > 0 ? opportunity.type[0] : "Other";
    }
    return opportunity.type.split(",")[0].trim();
  };
  const getAllTypes = (): string[] => {
    if (Array.isArray(opportunity.type)) {
      return opportunity.type;
    }
    return opportunity.type.split(",").map((type) => type.trim());
  };
  const formatDate = (dateString: string | Date | null) => {
    if (!dateString) return "N/A";
    try {
      if (dateString instanceof Date) {
        return format(dateString, "MMMM d, yyyy");
      } else {
        return format(parseISO(dateString), "MMMM d, yyyy");
      }
    } catch (error) {
      console.error("Error formatting date:", dateString, error);
      return "Invalid date";
    }
  };
  const formatCompactDate = (dateString: string | Date | null) => {
    if (!dateString) return "N/A";
    try {
      const date =
        dateString instanceof Date ? dateString : parseISO(dateString);
      return format(date, "MMM d, yyyy");
    } catch (error) {
      console.error("Error formatting compact date:", dateString, error);
      return "Invalid date";
    }
  };
  const getTypeIcon = (type: string) => {
    switch (type) {
      case "Hackathon":
        return "code-slash-outline";
      case "Competition":
        return "trophy-outline";
      case "Grant":
        return "cash-outline";
      case "Scholarship":
        return "school-outline";
      case "Conference":
        return "people-outline";
      case "Workshop/Programs":
        return "construct-outline";
      case "Internship":
        return "briefcase-outline";
      default:
        return "star-outline";
    }
  };
  const getTypeColor = (type: string) => {
    switch (type) {
      case "Hackathon":
        return "#F43F5E"; // Rose
      case "Competition":
        return "#3B82F6"; // Blue
      case "Grant":
        return "#8B5CF6"; // Violet
      case "Scholarship":
        return "#10B981"; // Emerald
      case "Conference":
        return "#334155"; // Slate
      case "Workshop/Programs":
        return "#F59E0B"; // Amber
      case "Internship":
        return "#06B6D4"; // Cyan
      default:
        return "#71717A"; // Gray
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case "Open":
        return "#10B981"; // Emerald
      case "Coming Soon":
        return "#F59E0B"; // Amber
      case "Ongoing":
        return "#3B82F6"; // Blue
      case "Closed":
        return "#EF4444"; // Red
      default:
        return "#71717A"; // Gray
    }
  };
  const handleWebsitePress = async () => {
    if (opportunity.website) {
      try {
        trackApplyNowClick(opportunity);
        await Linking.openURL(opportunity.website);
      } catch (error) {
        console.error("Error opening URL:", error);
      }
    }
  };
  const handleSharePress = async () => {
    try {
      const deepLinkWeb = `https://bcstem.ca/opportunity/${opportunity.id}`;
      const deepLinkApp = `opportunitydb://opportunity/${opportunity.id}`;
      const message = `Check out this STEM opportunity: ${opportunity.title} by ${opportunity.organization}\n\n${
        Platform.OS === "web" ? deepLinkWeb : deepLinkApp
      }`;

      trackShare(opportunity);
      await Share.share({
        message,
        title: opportunity.title,
        url: Platform.OS === "web" ? deepLinkWeb : deepLinkApp,
      });
    } catch (error) {
      console.error("Error sharing opportunity:", error);
    }
  };
  const handleClaimPress = () => {
    alert("New feature coming soon!");
  };
  const getDaysRemaining = () => {
    const deadlineString =
      opportunity.applicationCloseDate || opportunity.deadline;
    if (!deadlineString) return null;
    return getDaysUntilDate(deadlineString);
  };
  const calculatedStatus = calculateOpportunityStatus(opportunity);
  const deadlineInfo = getDaysRemaining();
  const hasDeadlineSoon =
    deadlineInfo &&
    (deadlineInfo.includes("today") ||
      deadlineInfo.includes("tomorrow") ||
      (deadlineInfo.includes("days left") && !deadlineInfo.includes("Future")));

  return (
    <SafeAreaView
      style={[styles.safeArea, { backgroundColor: c.background }]}
      data-oid="ta.wi9b"
    >
      <Animatable.View
        animation="fadeIn"
        duration={300}
        style={[styles.container, { backgroundColor: c.background }]}
        {...(Platform.OS === "web" && {
          className: "opportunity-detail-container",
        })}
        data-oid="dmwvbtz"
      >
        {/* App-like header with close button and actions */}
        <View
          style={[
            styles.header,
            {
              backgroundColor: c.header,
              borderBottomColor: c.border,
              borderTopColor: getTypeColor(getPrimaryType()),
            },
          ]}
          data-oid="9mbvba5"
        >
          <TouchableOpacity
            style={[styles.closeButton, { backgroundColor: c.closeBtn }]}
            onPress={onClose}
            hitSlop={{ top: 15, bottom: 15, left: 15, right: 15 }}
            data-oid="00t3q1b"
          >
            <Ionicons
              name="chevron-back"
              size={24}
              color={c.text}
              data-oid="shosd4:"
            />
          </TouchableOpacity>
          <View style={styles.headerActions} data-oid="nke-l2q">
            <TouchableOpacity
              style={[
                styles.headerActionButton,
                { backgroundColor: c.actionBtn },
              ]}
              onPress={() => setIsReportFormVisible(true)}
              data-oid="report-issue-header"
            >
              <Ionicons
                name="alert-circle-outline"
                size={22}
                color={c.text}
                data-oid="report-issue-icon"
              />
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.headerActionButton,
                { backgroundColor: c.actionBtn },
              ]}
              onPress={handleSharePress}
              data-oid="hy06bvg"
            >
              <Ionicons
                name="share-outline"
                size={22}
                color={c.text}
                data-oid="8z2b:t7"
              />
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.headerActionButton,
                { backgroundColor: c.actionBtn },
              ]}
              onPress={() => {
                if (!isAuthenticated) {
                  setAuthModalFeature("saved opportunities");
                  setIsAuthModalVisible(true);
                } else {
                  if (isFavorite) {
                    unsaveOpportunity(opportunity.id);
                    setIsFavorite(false);
                  } else {
                    saveOpportunity(opportunity.id);
                    setIsFavorite(true);
                  }
                }
              }}
              data-oid="gar6sy4"
            >
              <Ionicons
                name={isFavorite && isAuthenticated ? "bookmark" : "bookmark-outline"}
                size={22}
                color={c.text}
                data-oid=".pb_pim"
              />
            </TouchableOpacity>
          </View>
        </View>
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={true}
          data-oid="5gh5cvp"
        >
          {/* Title section */}
          <Animatable.View
            animation="fadeIn"
            duration={600}
            style={[
              styles.titleSection,
              { backgroundColor: c.card, borderBottomColor: c.border },
            ]}
            data-oid="wrn6a6f"
          >
            {/* Type and status badges */}
            <View style={styles.badgeContainer} data-oid="3fj3.9s">
              {/* type badge removed */}
              <View
                style={[
                  styles.statusBadge,
                  { backgroundColor: getStatusColor(calculatedStatus || 'Open') },
                ]}
                data-oid="6:hbew1"
              >
                <Text style={styles.statusBadgeText} data-oid="4n:8i32">
                  {(calculatedStatus || 'Open').toUpperCase()}
                </Text>
              </View>
              {/* Add interaction type as small badge if available */}
              {opportunity.interactionTypes &&
                opportunity.interactionTypes.length > 0 && (
                  <View
                    style={[
                      styles.interactionBadge,
                      { backgroundColor: c.badge, borderColor: c.border },
                    ]}
                    data-oid="vxh-ppc"
                  >
                    <Ionicons
                      name={
                        opportunity.interactionTypes[0]
                          .toLowerCase()
                          .includes("virtual")
                          ? "laptop-outline"
                          : opportunity.interactionTypes[0]
                                .toLowerCase()
                                .includes("hybrid")
                            ? "git-merge-outline"
                            : "person-outline"
                      }
                      size={12}
                      color={c.badgeText}
                      data-oid="meb3vpn"
                    />

                    <Text
                      style={[
                        styles.interactionBadgeText,
                        { color: c.badgeText },
                      ]}
                      data-oid="dvzo8j9"
                    >
                      {opportunity.interactionTypes[0]}
                    </Text>
                  </View>
                )}
            </View>
            {/* Title and organization */}
            <Text
              accessibilityRole="header"
              aria-level={1}
              style={[styles.titleText, { color: c.text }]}
              data-oid="n.ikyc4"
            >
              {opportunity.title}
            </Text>
            <View style={styles.organizationRow} data-oid="sk8o-uc">
              <Ionicons
                name="business-outline"
                size={16}
                color={c.text}
                data-oid="kz1621e"
              />

              <Text
                style={[styles.organizationText, { color: c.orgText }]}
                data-oid="nu6n.vp"
              >
                {opportunity.organization}
              </Text>
            </View>
          </Animatable.View>
          {/* Key information card */}
          <Animatable.View
            animation="fadeInUp"
            duration={600}
            delay={100}
            style={[
              styles.keyInfoCard,
              { backgroundColor: c.card, shadowColor: c.shadow },
            ]}
            data-oid="mf1mlqb"
          >
            {/* Deadline card */}
            {(opportunity.applicationCloseDate || opportunity.deadline) &&
              ((calculatedStatus || 'Open') === "Open" ||
                (calculatedStatus || 'Open') === "Coming Soon") && (
                <View
                  style={[
                    styles.deadlineCard,
                    hasDeadlineSoon
                      ? [
                          styles.urgentDeadlineCard,
                          {
                            backgroundColor: c.urgentBg,
                            borderLeftColor: c.urgentBorder,
                          },
                        ]
                      : {
                          backgroundColor: c.deadlineBg,
                          borderLeftColor: c.deadlineBorder,
                        },
                  ]}
                  data-oid="c_hs3-o"
                >
                  <View style={styles.deadlineIconContainer} data-oid="ggq6e8:">
                    <Ionicons
                      name={hasDeadlineSoon ? "time" : "calendar-outline"}
                      size={24}
                      color={hasDeadlineSoon ? c.urgentLabel : c.deadlineLabel}
                      data-oid="sr_:.il"
                    />
                  </View>
                  <View style={styles.deadlineContent} data-oid="fk714x_">
                    <Text
                      style={[
                        styles.deadlineLabel,
                        hasDeadlineSoon
                          ? { color: c.urgentLabel }
                          : { color: c.deadlineLabel },
                      ]}
                      data-oid="elv2xrz"
                    >
                      {hasDeadlineSoon
                        ? "Application Deadline Soon"
                        : "Application Deadline"}
                    </Text>
                    <Text
                      style={[
                        styles.deadlineDate,
                        hasDeadlineSoon
                          ? { color: c.urgentDate }
                          : { color: c.deadlineDate },
                      ]}
                      data-oid="w6acp2e"
                    >
                      {isSmallScreen
                        ? formatCompactDate(
                            opportunity.applicationCloseDate ||
                              opportunity.deadline ||
                              null,
                          )
                        : formatDate(
                            opportunity.applicationCloseDate ||
                              opportunity.deadline ||
                              null,
                          )}
                      {deadlineInfo && ` • ${deadlineInfo}`}
                    </Text>
                  </View>
                </View>
              )}
            {/* Key information grid */}
            <View style={styles.infoGrid} data-oid="9tn7q:0">
              <View
                style={[
                  styles.infoGridItem,
                  isVerySmallScreen && styles.infoGridItemFullWidth,
                ]}
                data-oid="ee1ym:k"
              >
                <Ionicons
                  name="calendar"
                  size={20}
                  color={theme === "dark" ? "#93C5FD" : "#3B82F6"}
                  data-oid="xgek2.2"
                />

                <View style={styles.infoGridItemContent} data-oid="zw10-5_">
                  <Text
                    style={[styles.infoGridItemLabel, { color: c.infoLabel }]}
                    data-oid="ifbhu0_"
                  >
                    Program Dates
                  </Text>
                  <Text
                    style={[styles.infoGridItemValue, { color: c.infoValue }]}
                    data-oid="qj.3z1r"
                  >
                    {isSmallScreen
                      ? formatCompactDate(opportunity.startDate)
                      : formatDate(opportunity.startDate)}
                    {opportunity.endDate !== opportunity.startDate &&
                      ` - ${isSmallScreen ? formatCompactDate(opportunity.endDate) : formatDate(opportunity.endDate)}`}
                  </Text>
                </View>
              </View>
              <View
                style={[
                  styles.infoGridItem,
                  isVerySmallScreen && styles.infoGridItemFullWidth,
                ]}
                data-oid="ax7ow1o"
              >
                <Ionicons
                  name="location"
                  size={20}
                  color={theme === "dark" ? "#F87171" : "#F43F5E"}
                  data-oid="1a27x9c"
                />

                <View style={styles.infoGridItemContent} data-oid="pkxjg7g">
                  <Text
                    style={[styles.infoGridItemLabel, { color: c.infoLabel }]}
                    data-oid="zghw71b"
                  >
                    Location
                  </Text>
                  <Text
                    style={[styles.infoGridItemValue, { color: c.infoValue }]}
                    data-oid="13_kteq"
                  >
                    {opportunity.location}
                  </Text>
                </View>
              </View>
              {(opportunity.applicationOpenDate ||
                opportunity.applicationCloseDate) && (
                <View
                  style={[
                    styles.infoGridItem,
                    isVerySmallScreen && styles.infoGridItemFullWidth,
                  ]}
                  data-oid="mk0bpgl"
                >
                  <Ionicons
                    name="document-text"
                    size={20}
                    color={theme === "dark" ? "#C084FC" : "#8B5CF6"}
                    data-oid="_kkh-nr"
                  />

                  <View style={styles.infoGridItemContent} data-oid="n8d-gj:">
                    <Text
                      style={[styles.infoGridItemLabel, { color: c.infoLabel }]}
                      data-oid="-xh9oka"
                    >
                      Application Period
                    </Text>
                    <Text
                      style={[styles.infoGridItemValue, { color: c.infoValue }]}
                      data-oid="ar_ihi:"
                    >
                      {opportunity.applicationOpenDate
                        ? isSmallScreen
                          ? formatCompactDate(opportunity.applicationOpenDate)
                          : formatDate(opportunity.applicationOpenDate)
                        : "Open"}
                      {opportunity.applicationCloseDate
                        ? ` - ${isSmallScreen ? formatCompactDate(opportunity.applicationCloseDate) : formatDate(opportunity.applicationCloseDate)}`
                        : ""}
                    </Text>
                  </View>
                </View>
              )}
              {opportunity.cost && (
                <View
                  style={[
                    styles.infoGridItem,
                    isVerySmallScreen && styles.infoGridItemFullWidth,
                  ]}
                  data-oid="nckh7bl"
                >
                  <Ionicons
                    name="cash"
                    size={20}
                    color={theme === "dark" ? "#34D399" : "#10B981"}
                    data-oid="seec.ji"
                  />

                  <View style={styles.infoGridItemContent} data-oid="0tnm::4">
                    <Text
                      style={[styles.infoGridItemLabel, { color: c.infoLabel }]}
                      data-oid="z0r:h4x"
                    >
                      Cost
                    </Text>
                    <Text
                      style={[styles.infoGridItemValue, { color: c.infoValue }]}
                      data-oid="u2lvhz1"
                    >
                      {opportunity.cost}
                    </Text>
                  </View>
                </View>
              )}
            </View>
          </Animatable.View>
          {/* Tab navigation */}
          <View
            style={[
              styles.tabNavigation,
              { backgroundColor: c.tag, borderColor: c.border },
            ]}
            data-oid="u4d7:2x"
          >
            <TouchableOpacity
              style={[
                styles.tabButton,
                activeTab === "details" && [
                  styles.activeTabButton,
                  { backgroundColor: c.card, shadowColor: c.shadow },
                ],
              ]}
              onPress={() => setActiveTab("details")}
              data-oid="i:0.std"
            >
              <Text
                style={[
                  styles.tabButtonText,
                  { color: c.tagText },
                  activeTab === "details" && [
                    styles.activeTabButtonText,
                    { color: c.infoValue },
                  ],
                ]}
                data-oid="t62r-5k"
              >
                Details
              </Text>
            </TouchableOpacity>
            {opportunity.eligibility && (
              <TouchableOpacity
                style={[
                  styles.tabButton,
                  activeTab === "eligibility" && [
                    styles.activeTabButton,
                    { backgroundColor: c.card, shadowColor: c.shadow },
                  ],
                ]}
                onPress={() => setActiveTab("eligibility")}
                data-oid="epcf-ie"
              >
                <Text
                  style={[
                    styles.tabButtonText,
                    { color: c.tagText },
                    activeTab === "eligibility" && [
                      styles.activeTabButtonText,
                      { color: c.infoValue },
                    ],
                  ]}
                  data-oid=":4xm0wa"
                >
                  Eligibility
                </Text>
              </TouchableOpacity>
            )}
          </View>
          {/* Tab content */}
          {activeTab === "details" ? (
            <Animatable.View
              animation="fadeIn"
              duration={500}
              style={styles.tabContent}
              data-oid="ck5q1zo"
            >
              {/* About this opportunity */}
              <View style={styles.contentSection} data-oid="xw4uc0z">
                <Text
                  style={[styles.sectionTitle, { color: c.sectionTitle }]}
                  data-oid="xl6hjhb"
                >
                  About this opportunity
                </Text>
                <Text
                  style={[styles.description, { color: c.desc }]}
                  data-oid="4eesf:0"
                >
                  {opportunity.description}
                </Text>
              </View>
              {/* Additional information cards */}
              <View style={styles.additionalInfoContainer} data-oid="hk4f9bd">
                {opportunity.gradeLevel &&
                  opportunity.gradeLevel.length > 0 && (
                    <View
                      style={[
                        styles.additionalInfoCard,
                        { backgroundColor: c.addCard },
                      ]}
                      data-oid="b2_2unk"
                    >
                      <View
                        style={styles.additionalInfoIconContainer}
                        data-oid="kvvq-:7"
                      >
                        <Ionicons
                          name="school"
                          size={24}
                          color={theme === "dark" ? "#93C5FD" : "#334155"}
                          data-oid="5h7mov9"
                        />
                      </View>
                      <View
                        style={styles.additionalInfoContent}
                        data-oid="42mpkv_"
                      >
                        <Text
                          style={[
                            styles.additionalInfoTitle,
                            { color: c.sectionTitle },
                          ]}
                          data-oid="y7_cd4v"
                        >
                          Grade Levels
                        </Text>
                        <Text
                          style={[styles.additionalInfoText, { color: c.desc }]}
                          data-oid="vc2dt1."
                        >
                          {opportunity.gradeLevel.join(", ")}
                        </Text>
                      </View>
                    </View>
                  )}
              </View>
              {/* Tags section */}
              {opportunity.tags.length > 0 && (
                <View style={styles.contentSection} data-oid=":pfvueo">
                  <Text
                    style={[styles.sectionTitle, { color: c.sectionTitle }]}
                    data-oid="w5j83d3"
                  >
                    Tags
                  </Text>
                  <View style={styles.tagsContainer} data-oid="98u22pd">
                    {opportunity.tags.map((tag, index) => (
                      <View
                        key={index}
                        style={[styles.tag, { backgroundColor: c.tag }]}
                        data-oid="ub.sup0"
                      >
                        <Text
                          style={[styles.tagText, { color: c.tagText }]}
                          data-oid="nn2hun3"
                        >
                          {tag}
                        </Text>
                      </View>
                    ))}
                  </View>
                </View>
              )}
            </Animatable.View>
          ) : (
            <Animatable.View
              animation="fadeIn"
              duration={500}
              style={styles.tabContent}
              data-oid="-vun8d-"
            >
              {/* Eligibility tab content */}
              <View style={styles.contentSection} data-oid="xjtullq">
                <Text
                  style={[styles.sectionTitle, { color: c.sectionTitle }]}
                  data-oid="7qkyyuj"
                >
                  Eligibility Requirements
                </Text>
                <Text
                  style={[styles.description, { color: c.desc }]}
                  data-oid="m8:i7v-"
                >
                  {opportunity.eligibility}
                </Text>
              </View>
              {/* Grade levels section in eligibility tab */}
              {opportunity.gradeLevel && opportunity.gradeLevel.length > 0 && (
                <View style={styles.contentSection} data-oid="p4qyji3">
                  <Text
                    style={[styles.sectionTitle, { color: c.sectionTitle }]}
                    data-oid="gtpo2cc"
                  >
                    Grade Levels
                  </Text>
                  <View
                    style={[
                      styles.additionalInfoCard,
                      { backgroundColor: c.addCard },
                    ]}
                    data-oid="t1ix2.j"
                  >
                    <View
                      style={styles.additionalInfoIconContainer}
                      data-oid="gvqso25"
                    >
                      <Ionicons
                        name="school"
                        size={24}
                        color={theme === "dark" ? "#93C5FD" : "#334155"}
                        data-oid="jqruxtx"
                      />
                    </View>
                    <View
                      style={styles.additionalInfoContent}
                      data-oid="o:3z40p"
                    >
                      <Text
                        style={[styles.additionalInfoText, { color: c.desc }]}
                        data-oid="hhie_o2"
                      >
                        {opportunity.gradeLevel.join(", ")}
                      </Text>
                    </View>
                  </View>
                </View>
              )}
            </Animatable.View>
          )}
          {/* Info for hosts */}
          {!opportunity.isClaimed && (
            <View style={styles.claimSection} data-oid="49kua2y">
              <View
                style={[
                  styles.claimBox,
                  { backgroundColor: c.claimBox, borderColor: c.claimBorder },
                ]}
                data-oid="bt49_j8"
              >
                <View style={styles.claimIconContainer} data-oid="hz2r15k">
                  <Ionicons
                    name="shield-checkmark-outline"
                    size={28}
                    color={c.claimBtn}
                    data-oid="iwoj.1i"
                  />
                </View>
                <View style={styles.claimContent} data-oid="af7_8px">
                  <Text
                    style={[styles.claimTitle, { color: c.claimTitle }]}
                    data-oid="z-zy0ky"
                  >
                    Is this your opportunity?
                  </Text>
                  <Text
                    style={[styles.claimDescription, { color: c.claimDesc }]}
                    data-oid="w.7pz10"
                  >
                    If you represent this organization, you can claim this
                    listing to update information, respond to questions, and
                    receive direct applications.
                  </Text>
                  <TouchableOpacity
                    style={styles.claimButton}
                    onPress={handleClaimPress}
                    data-oid="8fkdgkj"
                  >
                    <Text
                      style={[
                        styles.claimButtonText,
                        { color: c.claimBtnText },
                      ]}
                      data-oid="1oqhehc"
                    >
                      Claim this listing
                    </Text>
                    <Ionicons
                      name="arrow-forward"
                      size={16}
                      color={c.claimBtn}
                      data-oid="s593e2-"
                    />
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          )}
          {/* Footer with timestamp and report button */}
          <View style={styles.footer} data-oid="2f77ra.">
            <Text
              style={[styles.footerText, { color: c.footerText }]}
              data-oid="qxzgfmu"
            >
              Last updated:{" "}
              {opportunity.updatedAt
                ? formatDate(opportunity.updatedAt)
                : "N/A"}
            </Text>
            <TouchableOpacity
              style={[styles.reportButton, { backgroundColor: c.reportBtn }]}
              onPress={() => setIsReportFormVisible(true)}
              data-oid="6l4qn3d"
            >
              <Ionicons
                name="alert-circle-outline"
                size={14}
                color={c.reportBtnText}
                data-oid="h:qls6s"
              />

              <Text
                style={[styles.reportButtonText, { color: c.reportBtnText }]}
                data-oid="tzdwnw0"
              >
                Report an issue
              </Text>
            </TouchableOpacity>
          </View>
          <View style={{ height: 80 }} data-oid="83dnz7-" />
        </ScrollView>
        {/* Sticky Apply button */}
        <Animatable.View
          animation="fadeInUp"
          duration={400}
          style={[
            styles.stickyApplyContainer,
            {
              backgroundColor: c.stickyBg,
              borderTopColor: c.stickyBorder,
              shadowColor: c.shadow,
            },
          ]}
          data-oid="n7dnhwg"
        >
          <TouchableOpacity
            style={[
              styles.stickyApplyButton,
              { backgroundColor: getTypeColor(getPrimaryType()) },
            ]}
            onPress={handleWebsitePress}
            data-oid="umfo.3c"
          >
            <Text style={styles.stickyApplyButtonText} data-oid="1f9jzr9">
              Apply Now
            </Text>
            <Ionicons
              name="arrow-forward"
              size={18}
              color="#fff"
              data-oid=":9itlp."
            />
          </TouchableOpacity>
        </Animatable.View>
      </Animatable.View>

      {/* Feedback Modal */}
      <FeedbackModal
        isVisible={isReportFormVisible}
        onClose={() => setIsReportFormVisible(false)}
        opportunity={opportunity}
        data-oid="yptxd5."
      />

      {/* Auth Modal */}
      <AuthRequiredModal
        visible={isAuthModalVisible}
        onClose={() => setIsAuthModalVisible(false)}
        featureName={authModalFeature}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: "#f8fafc",
  },
  container: {
    flex: 1,
    backgroundColor: "#f8fafc",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: isVerySmallScreen ? 12 : 16,
    backgroundColor: "#FFFFFF",
    borderBottomWidth: 1,
    borderBottomColor: "#E4E4E7",
    borderTopWidth: 4,
  },
  closeButton: {
    width: 40,
    height: 40,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 20,
    backgroundColor: "#F4F4F5",
  },
  headerActions: {
    flexDirection: "row",
  },
  headerActionButton: {
    width: 40,
    height: 40,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 20,
    backgroundColor: "#F4F4F5",
    marginLeft: 8,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 40,
  },
  titleSection: {
    backgroundColor: "#ffffff",
    padding: isVerySmallScreen ? 16 : 20,
    borderBottomWidth: 1,
    borderBottomColor: "#E4E4E7",
  },
  badgeContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginBottom: 12,
    alignItems: "center",
  },
  typeBadge: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    marginRight: 8,
    marginBottom: 8,
  },
  typeBadgeText: {
    color: "#FFF",
    fontSize: 12,
    fontWeight: "600",
    marginLeft: 4,
    fontFamily: "Inter-SemiBold",
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    marginRight: 8,
    marginBottom: 8,
  },
  statusBadgeText: {
    color: "#FFF",
    fontSize: 12,
    fontWeight: "700",
    fontFamily: "Inter-Bold",
    letterSpacing: 0.5,
  },
  interactionBadge: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F0F0F0",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E0E0E0",
    marginBottom: 8,
  },
  interactionBadgeText: {
    fontSize: 11,
    color: "#4B5563",
    marginLeft: 4,
    fontFamily: "Inter-Medium",
  },
  titleText: {
    fontSize: isVerySmallScreen ? 22 : isSmallScreen ? 24 : 28,
    fontWeight: "bold",
    color: "#1F2937",
    marginBottom: 12,
    fontFamily: "Inter-Bold",
  },
  organizationRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  organizationText: {
    fontSize: 16,
    color: "#4B5563",
    marginLeft: 8,
    fontFamily: "Inter-Medium",
  },
  keyInfoCard: {
    margin: isVerySmallScreen ? 12 : 16,
    backgroundColor: "#FFF",
    borderRadius: 16,
    paddingVertical: isVerySmallScreen ? 12 : 16,
    paddingHorizontal: isVerySmallScreen ? 16 : 20,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  deadlineCard: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#fff8e1",
    padding: 16,
    borderRadius: 12,
    marginBottom: 16,
    borderLeftWidth: 4,
    borderLeftColor: "#d97706",
  },
  urgentDeadlineCard: {
    backgroundColor: "#ef4444",
    borderLeftColor: "#b91c1c",
  },
  deadlineIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "rgba(217, 119, 6, 0.2)",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  deadlineContent: {
    flex: 1,
  },
  deadlineLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: "#d97706",
    marginBottom: 4,
    fontFamily: "Inter-SemiBold",
  },
  urgentDeadlineLabel: {
    color: "#fff",
  },
  deadlineDate: {
    fontSize: 14,
    color: "#92400e",
    fontFamily: "Inter-Regular",
  },
  urgentDeadlineDate: {
    color: "#fff",
  },
  infoGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginHorizontal: -8,
  },
  infoGridItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    width: "50%",
    paddingHorizontal: 8,
    marginBottom: 16,
  },
  infoGridItemFullWidth: {
    width: "100%",
  },
  infoGridItemContent: {
    marginLeft: 12,
    flex: 1,
  },
  infoGridItemLabel: {
    fontSize: 12,
    color: "#64748b",
    marginBottom: 4,
    fontFamily: "Inter-Regular",
  },
  infoGridItemValue: {
    fontSize: 14,
    color: "#334155",
    fontWeight: "500",
    fontFamily: "Inter-Medium",
  },
  tabNavigation: {
    flexDirection: "row",
    marginHorizontal: isVerySmallScreen ? 12 : 16,
    backgroundColor: "#E2E8F0",
    borderRadius: 8,
    padding: 4,
    borderWidth: 1,
    borderColor: "transparent",
    marginBottom: isVerySmallScreen ? 12 : 16,
  },
  tabButton: {
    flex: 1,
    paddingVertical: 10,
    alignItems: "center",
    borderRadius: 6,
  },
  activeTabButton: {
    backgroundColor: "#FFF",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  tabButtonText: {
    fontSize: 14,
    color: "#64748b",
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },
  activeTabButtonText: {
    color: "#334155",
  },
  tabContent: {
    paddingHorizontal: isVerySmallScreen ? 12 : 16,
  },
  contentSection: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "600",
    color: "#334155",
    marginBottom: 12,
    fontFamily: "Inter-SemiBold",
  },
  description: {
    fontSize: 15,
    lineHeight: 24,
    color: "#475569",
    fontFamily: "Inter-Regular",
  },
  additionalInfoContainer: {
    marginBottom: 24,
  },
  additionalInfoCard: {
    flexDirection: "row",
    backgroundColor: "#f1f5f9",
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
  },
  additionalInfoIconContainer: {
    alignItems: "center",
    justifyContent: "center",
    marginRight: 16,
  },
  additionalInfoContent: {
    flex: 1,
  },
  additionalInfoTitle: {
    fontSize: 15,
    fontWeight: "600",
    color: "#334155",
    marginBottom: 4,
    fontFamily: "Inter-SemiBold",
  },
  additionalInfoText: {
    fontSize: 14,
    color: "#475569",
    fontFamily: "Inter-Regular",
  },
  tagsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: 8,
  },
  tag: {
    backgroundColor: "#f1f5f9",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    marginRight: 8,
    marginBottom: 8,
  },
  tagText: {
    fontSize: 13,
    color: "#64748b",
    fontFamily: "Inter-Medium",
  },
  claimSection: {
    paddingHorizontal: isVerySmallScreen ? 12 : 16,
    marginBottom: 24,
  },
  claimBox: {
    flexDirection: "row",
    backgroundColor: "#eff6ff",
    borderRadius: 16,
    padding: 16,
    borderWidth: 1,
    borderColor: "#bfdbfe",
  },
  claimIconContainer: {
    alignItems: "center",
    justifyContent: "center",
    marginRight: 16,
  },
  claimContent: {
    flex: 1,
  },
  claimTitle: {
    fontSize: 16,
    fontWeight: "600",
    color: "#1e40af",
    marginBottom: 8,
    fontFamily: "Inter-SemiBold",
  },
  claimDescription: {
    fontSize: 14,
    color: "#3b82f6",
    lineHeight: 20,
    marginBottom: 12,
    fontFamily: "Inter-Regular",
  },
  claimButton: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
  },
  claimButtonText: {
    fontSize: 14,
    fontWeight: "600",
    color: "#3498db",
    marginRight: 4,
    fontFamily: "Inter-SemiBold",
  },
  footer: {
    paddingHorizontal: isVerySmallScreen ? 12 : 16,
    marginVertical: 20,
    alignItems: "center",
  },
  footerText: {
    fontSize: 12,
    color: "#94a3b8",
    textAlign: "center",
    marginBottom: 10,
    fontFamily: "Inter-Regular",
  },
  reportButton: {
    flexDirection: "row",
    alignItems: "center",
    padding: 8,
    borderRadius: 20,
    backgroundColor: "#F1F5F9",
  },
  reportButtonText: {
    fontSize: 12,
    color: "#64748b",
    marginLeft: 6,
    fontFamily: "Inter-Medium",
  },
  stickyApplyContainer: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    paddingVertical: isVerySmallScreen ? 10 : 12,
    paddingHorizontal: isVerySmallScreen ? 12 : 16,
    borderTopWidth: 1,
    borderTopColor: "#E2E8F0",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 5,
  },
  stickyApplyButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 14,
    borderRadius: 12,
  },
  stickyApplyButtonText: {
    fontSize: 16,
    fontWeight: "600",
    color: "#fff",
    marginRight: 8,
    fontFamily: "Inter-SemiBold",
  },
});

export default OpportunityDetail;
